import React, { useState, useEffect } from 'react';
import { Fab } from '@mui/material';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import { useTranslation } from 'react-i18next';

/**
 * Back-to-top FAB, bottom-left (opposite Maya FAB on bottom-right).
 * Appears after 400px scroll.
 * Uses CSS transform instead of MUI Zoom to avoid hydration mismatch
 * (Zoom adds visibility/transform via effect, mismatching prerender).
 */
export default function BackToTop() {
  const { t } = useTranslation();
  const [visible, setVisible] = useState(false);

  /* Hydration-safe: render nothing on first paint so the empty output matches
     Puppeteer's prerender (which also skips via navigator.webdriver).
     After hydration, useEffect flips `mounted` → widget appears. */
  const isPrerender = typeof navigator !== 'undefined' && navigator.webdriver;
  const [mounted, setMounted] = useState(false);
  useEffect(() => { if (!isPrerender) setMounted(true); }, [isPrerender]);

  useEffect(() => {
    const handleScroll = () => {
      setVisible(window.scrollY > 400);
    };
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  if (isPrerender || !mounted) return null;

  return (
    <Fab
      onClick={scrollToTop}
      size="small"
      aria-label={t('a11y.backToTop', 'Volver arriba')}
      sx={{
        position: 'fixed',
        bottom: 24,
        left: 24,
        bgcolor: '#1E3A5F',
        color: '#FFFFFF',
        zIndex: 1000,
        '&:hover': { bgcolor: '#1A3152' },
        transform: visible ? 'scale(1)' : 'scale(0)',
        transition: 'transform 0.2s ease-in-out',
        pointerEvents: visible ? 'auto' : 'none',
      }}
    >
      <KeyboardArrowUpIcon />
    </Fab>
  );
}
